/**
 * Per-tenant branding, baked into the bundle at build time. Each tenant sets
 * VITE_BRAND_NAME (display name, e.g. "Acme") and optionally VITE_WORKSPACE_NAME
 * (the short topbar badge) in its `publish-admin-v2` CI job; Vite exposes VITE_-prefixed
 * env vars on import.meta.env. Unset clients fall back to a neutral label rather than
 * another tenant's name, so a repo that forgets to brand ships generic chrome.
 */

const RAW_BRAND = import.meta.env.VITE_BRAND_NAME?.trim();

/** Tenant display name used in the login subtitle, route titles and other branded copy. */
export const BRAND_NAME = RAW_BRAND || 'Admin';

/**
 * Tab/window title for non-route contexts (login, initial paint). Branded tenants get
 * "<Brand> Admin"; an unbranded build collapses to the bare "Admin" (no "Admin Admin").
 */
export const APP_TITLE = RAW_BRAND ? `${RAW_BRAND} Admin` : 'Admin';

/**
 * Short uppercase badge shown in the topbar pill next to the Sleed logo. Defaults to
 * the brand name uppercased when VITE_WORKSPACE_NAME is unset.
 */
export const WORKSPACE_BADGE = (import.meta.env.VITE_WORKSPACE_NAME?.trim() || BRAND_NAME).toUpperCase();

/**
 * Storefront origin for the site-context menu. Unlike the brand/badge (baked per build),
 * this differs per deploy *environment* (staging vs production) while one artifact is built,
 * so the authoritative source is the deploy-time runtime config — `config.js` writes
 * `window.__ADMIN_V2_CONFIG__.storefrontUrl` from the target environment's deploy config.
 * The build-time VITE_STOREFRONT_URL is a fallback for local/preview builds with no config.js.
 * Undefined when neither is set → the site-context menu is hidden rather than pointing nowhere.
 */
// Structural cast (the client.ts pattern): `globalThis.X` only sees `var` declarations, not Window
// interface members — accessing the runtime config directly is a TS7017 under the typecheck script.
export const STOREFRONT_URL =
	(globalThis as { __ADMIN_V2_CONFIG__?: { storefrontUrl?: string } }).__ADMIN_V2_CONFIG__?.storefrontUrl?.trim() ||
	import.meta.env.VITE_STOREFRONT_URL?.trim() ||
	undefined;
